package kz.dentalcrm.app.core.base

import androidx.lifecycle.ViewModel
import kotlinx.coroutines.CoroutineDispatcher
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.FlowPreview
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.cancelChildren
import kotlinx.coroutines.channels.Channel
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.debounce
import kotlinx.coroutines.flow.receiveAsFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kz.dentalcrm.app.core.network.ErrorContent
import org.koin.core.component.KoinComponent
import kotlin.coroutines.CoroutineContext

abstract class BaseStateViewModel<Event : ViewEvent, UiState, Effect : ViewSideEffect>(
    jobDispatcher: CoroutineDispatcher = Dispatchers.Default,
) : ViewModel(), CoroutineScope, KoinComponent {

    abstract fun setInitialState(): UiState

    abstract fun handleEvents(event: Event)

    protected val dead = ErrorContent.Dead(title = null, message = null)

    private val initialState: UiState by lazy { setInitialState() }

    private val initialError: ViewError? = null

    private val _viewState: MutableStateFlow<UiState> = MutableStateFlow(initialState)
    open val viewState: StateFlow<UiState> = _viewState

    private val _viewError: MutableStateFlow<ViewError?> = MutableStateFlow(initialError)
    open val viewError: StateFlow<ViewError?> = _viewError

    private val _event: MutableSharedFlow<Event> = MutableSharedFlow()

    private val _effect: Channel<Effect> = Channel()

    @OptIn(FlowPreview::class)
    val effect: Flow<Effect> get() = _effect.receiveAsFlow().debounce(DEBOUNCE_MILLIS)

    override val coroutineContext: CoroutineContext = jobDispatcher + SupervisorJob()

    init {
        subscribeToEvents()
    }

    private fun subscribeToEvents() {
        launch(Dispatchers.Main.immediate) {
            _event.collect {
                handleEvents(it)
            }
        }
    }

    protected fun handleDefaultError(
        errorContent: ErrorContent,
    ) {
        when (errorContent) {
            is ErrorContent.Dead -> {
                setErrorState {
                    ViewError.DeadError(errorContent.title, errorContent.message)
                }
            }

            is ErrorContent.Alert -> {
                setErrorState { ViewError.AlertError(errorContent.title, errorContent.message) }
            }

            is ErrorContent.Validation -> {
                errorContent.content?.let {
                    handleValidationError(it)
                }
            }
        }
    }


    /**
     * When it requires to handle validation error, in child class parse json required.
     * */
    open fun handleValidationError(content: HashMap<String, String>) {}

    fun setEvent(event: Event) {
        launch(Dispatchers.Main.immediate) { _event.emit(event) }
    }

    protected fun setState(reducer: UiState.() -> UiState) {
        val newState = viewState.value.reducer()
        _viewState.update { newState }
    }

    protected fun setErrorState(reducer: ViewError?.() -> ViewError?) {
        val newErrorState = viewError.value.reducer()
        _viewError.update { newErrorState }
    }

    protected fun setEffect(builder: () -> Effect) {
        val effectValue = builder()
        launch { _effect.trySend(effectValue) }
    }

    override fun onCleared() {
        coroutineContext.cancelChildren()
    }

    companion object {
        private const val DEBOUNCE_MILLIS = 250L
    }
}